// src/services/ecrfTypes.js
//
// SourceX Roadmap — PHASE 1b: the eCRF parse result schema.
//
// The blank eCRF is the study's MASTER SUPERSET: every question the study
// could ever ask, organized by form (not by visit), carrying the real field
// types, option lists, and — critically — the branching/skip logic. The SoA
// says *when*; the eCRF says *what*. This module is the canonical shape of a
// parsed eCRF plus the pure helpers that operate on it.
//
// Framework-free (no Firebase, no React) so it is unit-testable. Mirrors the
// conventions of protocolTypes.js.
//
// ---------------------------------------------------------------------------
// WHERE THE PIPELINE LIVES vs WHAT THIS FILE IS
// ---------------------------------------------------------------------------
// Reading the PDF, chunking it, calling the model, MERGING the chunk results,
// and RESOLVING branch label-references to field ids all happen SERVER-SIDE in
// functions/ecrfExtraction.js — that is the only place they run, and CRA cannot
// import from functions/ (nor that file from src/). Deliberately NOT duplicated
// here: merge and branch resolution are the two subtlest pieces of Phase 1b and
// two copies would drift.
//
// This file is the CLIENT-side half: the schema contract, factories for the
// review UI, validation, and reporting helpers. It consumes the finished,
// merged, resolved extraction and never re-derives it.
//
// The small overlap that does exist — `normKey` and the id hashing — MUST stay
// in sync with functions/ecrfExtraction.js. That mirrors what the repo already
// does for FIELD_TYPES between protocolTypes.js and soaExtraction.js.
//
// Field types are imported from templateSchema rather than
// redeclared, because the entire point of parsing the eCRF is that its fields
// eventually become template questions — a type this file could emit but the
// template couldn't render would be a silent dead end.
//
// WHY BRANCHING IS FIRST-CLASS HERE (roadmap §3.1): flattening skip logic into
// prose is lossy and unrecoverable. A source document that shows every
// conditional field unconditionally is not the eCRF's form — it is a longer,
// wrong form. So `visible_when` survives ingestion as structured data and maps
// directly onto the template's `visibleWhen` in templateSchema.js.

import {
  FIELD_TYPES,
  normFieldType,
  stableHash,
  CONDITION_OPS,
} from "./templateSchema";

export { FIELD_TYPES, normFieldType };

// Bump when the eCRF extraction schema changes in a way that should invalidate
// stored extractions. Independent of EXTRACTION_VERSION (protocols/SOA) so the
// two pipelines can version separately.
export const ECRF_EXTRACTION_VERSION = 1;

// Lifecycle of an uploaded eCRF document. Deliberately identical in spirit to
// PROTOCOL_STATUS so the two review UIs can share affordances.
export const ECRF_STATUS = Object.freeze({
  UPLOADED: "uploaded",
  PROCESSING: "processing",
  // Queued with the asynchronous batch reader and waiting on results. Distinct
  // from PROCESSING because nothing is running for us right now — the page
  // should say "come back later", not spin.
  BATCH_PENDING: "batch_pending",
  NEEDS_REVIEW: "needs_review",
  APPROVED: "approved",
  FAILED: "failed",
});

export const ECRF_STATUS_VALUES = Object.freeze(Object.values(ECRF_STATUS));

// At or below this, the UI flags a form/field as needing a human look.
export const LOW_CONFIDENCE_THRESHOLD = 0.9;

// How a field was read. Text extraction is the cheap default; vision is the
// per-form fallback for pages whose embedded text is missing or garbled.
// Recorded per form so cost and quality can be attributed after the fact.
export const EXTRACT_METHODS = Object.freeze(["text", "vision", "mixed"]);

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------
// eCRF ids are DETERMINISTIC, derived from the form/field name rather than
// randomly generated. Two reasons:
//   1. Re-parsing the same eCRF (a retry, or a v2 of the pipeline) must produce
//      the same ids, or the cached SoA<->eCRF join from Phase 2 is invalidated
//      and every mapping has to be re-reviewed by a human for no reason.
//   2. Branching arrives from the model as *label* references; resolving those
//      to ids is only stable if ids are a function of the label.

export const ecrfFormId = (formName) =>
  `f_${stableHash(normKey(formName))}`;

export const ecrfFieldId = (formName, fieldLabel) =>
  `fd_${stableHash(`${normKey(formName)}|${normKey(fieldLabel)}`)}`;

/**
 * Aggressive normalization for MATCHING ONLY — never for display.
 * eCRF and SoA documents name the same thing differently ("Wt (kg)" vs
 * "Weight"), and the same eCRF will spell a field differently in its branching
 * rule than in its field list. Strips punctuation, collapses whitespace, drops
 * a trailing parenthetical unit, and lowercases.
 */
export const normKey = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\([^)]*\)\s*$/, "") // trailing "(kg)", "(mmHg)", "(specify)"
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

/** A single eCRF field (one question on one form). */
export const makeEcrfField = (init = {}) => {
  const label = String(init.label || "").trim().slice(0, 500);
  const type = normFieldType(init.type);
  const field = {
    field_id: init.field_id || ecrfFieldId(init.form_name || "", label),
    label,
    type,
    required: init.required === true,
    source_page: toInt(init.source_page),
    confidence: clampConf(init.confidence),
  };

  if (init.oid) field.oid = String(init.oid).slice(0, 120);

  if (type === "Select" || type === "MultiSelect") {
    field.options = uniqStrings(init.options).slice(0, 60);
  }

  // Coded values: EDC systems store "1"/"0" behind "Yes"/"No". Preserved so a
  // later export can round-trip, but never shown to a coordinator.
  if (init.codes && typeof init.codes === "object" && !Array.isArray(init.codes)) {
    const codes = {};
    Object.keys(init.codes)
      .slice(0, 60)
      .forEach((k) => {
        codes[String(k)] = String(init.codes[k]);
      });
    if (Object.keys(codes).length) field.codes = codes;
  }

  if (type === "Number") {
    if (Array.isArray(init.units) && init.units.length) {
      field.units = uniqStrings(init.units).slice(0, 6);
      field.unit = init.unit && field.units.includes(init.unit) ? init.unit : field.units[0];
    } else if (init.unit) {
      field.unit = String(init.unit).slice(0, 40);
    }
  }

  // Repeating groups become a Table question downstream. The group id ties
  // sibling fields together so assembly knows they are columns of one log
  // rather than unrelated repeated questions.
  if (init.group_id) field.group_id = String(init.group_id).slice(0, 120);
  if (init.group_name) field.group_name = String(init.group_name).slice(0, 200);

  // Branching, as it arrived from the model: label references, unresolved.
  // resolveBranchRefs() converts these to field_id references.
  if (init.visible_when) field.visible_when = init.visible_when;

  return field;
};

/** A form: a named group of fields, spanning one or more eCRF pages. */
export const makeEcrfForm = (init = {}) => {
  const name = String(init.name || "").trim().slice(0, 300);
  return {
    form_id: init.form_id || ecrfFormId(name),
    name,
    ...(init.oid ? { oid: String(init.oid).slice(0, 120) } : {}),
    source_pages: uniqInts(init.source_pages),
    extract_method: EXTRACT_METHODS.includes(init.extract_method)
      ? init.extract_method
      : "text",
    confidence: clampConf(init.confidence),
    fields: (Array.isArray(init.fields) ? init.fields : []).map((f) =>
      makeEcrfField({ ...f, form_name: name })
    ),
  };
};

/** An empty, schema-correct extraction — safe default for the UI. */
export const emptyEcrfExtraction = () => ({
  ecrf_id: "",
  study_id: "",
  source_pages: [],
  page_count: 0,
  forms: [],
  warnings: [],
  extraction_version: ECRF_EXTRACTION_VERSION,
});

// ---------------------------------------------------------------------------
// Small coercion helpers (coerce-don't-reject, matching soaExtraction.js)
// ---------------------------------------------------------------------------

export const clampConf = (c) => {
  const n = Number(c);
  // Missing confidence means the extractor didn't express doubt. Treating that
  // as 1.0 matches soaExtraction.js's clampConf and keeps un-annotated output
  // from being drowned in false "needs review" flags.
  if (!Number.isFinite(n)) return 1;
  return Math.max(0, Math.min(1, n));
};

const toInt = (n) => {
  const v = parseInt(n, 10);
  return Number.isFinite(v) ? v : 0;
};

const uniqStrings = (a) =>
  [...new Set((Array.isArray(a) ? a : []).map((x) => String(x).trim()).filter(Boolean))];

const uniqInts = (a) =>
  [...new Set((Array.isArray(a) ? a : []).map(toInt).filter((n) => n > 0))].sort(
    (x, y) => x - y
  );

// ---------------------------------------------------------------------------
// Reporting helpers
// ---------------------------------------------------------------------------

export const countEcrfFields = (extraction) =>
  (extraction?.forms || []).reduce((n, f) => n + (f.fields || []).length, 0);

/** Fields/forms the reviewer should scrutinize. */
export const lowConfidenceEcrfCount = (extraction) => {
  let n = 0;
  (extraction?.forms || []).forEach((form) => {
    if (form.confidence < LOW_CONFIDENCE_THRESHOLD) n += 1;
    (form.fields || []).forEach((f) => {
      if (f.confidence < LOW_CONFIDENCE_THRESHOLD) n += 1;
    });
  });
  return n;
};

/**
 * Structural validation. Returns human-readable problems; empty means usable.
 * Strict about anything that would corrupt addressing or branching, permissive
 * about content (that is the reviewer's job).
 */
export const validateEcrfExtraction = (extraction) => {
  const problems = [];
  if (!extraction || typeof extraction !== "object") {
    return ["Extraction is missing or not an object."];
  }
  if (!Array.isArray(extraction.forms)) return ["forms is not an array."];

  const allFieldIds = new Set();
  const seenFormIds = new Set();

  extraction.forms.forEach((form, fi) => {
    const where = form?.name || `form[${fi}]`;
    if (!form || typeof form !== "object") {
      problems.push(`${where} is not an object.`);
      return;
    }
    if (!form.name || !String(form.name).trim()) {
      problems.push(`form[${fi}] has no name.`);
    }
    if (form.form_id) {
      if (seenFormIds.has(form.form_id)) {
        problems.push(`${where} has duplicate form_id "${form.form_id}".`);
      }
      seenFormIds.add(form.form_id);
    }
    const seenFieldIds = new Set();
    (Array.isArray(form.fields) ? form.fields : []).forEach((f, xi) => {
      const fwhere = `${where} / field[${xi}]`;
      if (!f || typeof f !== "object") {
        problems.push(`${fwhere} is not an object.`);
        return;
      }
      if (!f.label || !String(f.label).trim()) problems.push(`${fwhere} has no label.`);
      if (!f.field_id) {
        problems.push(`${fwhere} has no field_id.`);
      } else {
        if (seenFieldIds.has(f.field_id)) {
          problems.push(
            `${fwhere} has duplicate field_id "${f.field_id}" within its form.`
          );
        }
        seenFieldIds.add(f.field_id);
        allFieldIds.add(f.field_id);
      }
      if (!FIELD_TYPES.includes(f.type)) {
        problems.push(`${fwhere} has unknown type "${f.type}".`);
      }
      if (
        (f.type === "Select" || f.type === "MultiSelect") &&
        (!Array.isArray(f.options) || f.options.length === 0)
      ) {
        problems.push(`${fwhere} is "${f.type}" but has no options.`);
      }
    });
  });

  // Branch integrity — only meaningful after resolveBranchRefs.
  walkResolvedConds(extraction).forEach(({ cond, where }) => {
    if (cond.field_id && !allFieldIds.has(cond.field_id)) {
      problems.push(`${where} branches on unknown field_id "${cond.field_id}".`);
    }
    if (cond.op && !CONDITION_OPS.includes(cond.op)) {
      problems.push(`${where} uses unknown operator "${cond.op}".`);
    }
  });

  return problems;
};

const walkResolvedConds = (extraction) => {
  const out = [];
  const walk = (cond, where) => {
    if (!cond || typeof cond !== "object") return;
    if (Array.isArray(cond.all)) return cond.all.forEach((c) => walk(c, where));
    if (Array.isArray(cond.any)) return cond.any.forEach((c) => walk(c, where));
    if (cond.not) return walk(cond.not, where);
    out.push({ cond, where });
  };
  (extraction?.forms || []).forEach((form) =>
    (form.fields || []).forEach((f) => {
      if (f.visible_when) walk(f.visible_when, `${form.name} / "${f.label}"`);
    })
  );
  return out;
};
